<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">directions_walk</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Passengers</h2>
          <p class="text-sm text-gray-500">Manage passenger accounts.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Search -->
          <div class="relative w-full sm:w-64">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Search</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="q"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search passengers..."
              />
            </div>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in ['Name', 'Email', 'Contact Number', 'Rating', '']"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody v-if="passengers.length" class="relative">
              <tr
                v-for="(passenger, index) in passengers"
                :key="passenger._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Name -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <img
                      v-if="passenger.profileImage"
                      class="object-cover flex-shrink-0 w-10 h-10 rounded-full"
                      :src="passenger.profileImage"
                      :alt="personLabel(passenger)"
                    />
                    <span
                      v-else
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(passenger))"
                    >{{ initials(personLabel(passenger)) }}</span>
                    <p class="font-medium text-gray-900">
                      {{ personLabel(passenger, "Unknown") }}
                    </p>
                  </div>
                </td>
                <!-- Email -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ passenger.email || "—" }}
                </td>
                <!-- Contact number -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ passenger.phoneNumber || "—" }}
                </td>
                <!-- Rating -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-1">
                    <i class="text-base text-yellow-400 material-icons-round">star</i>
                    <span class="font-medium text-gray-700">
                      {{ passenger.rating || "0.0" }}
                    </span>
                  </div>
                </td>
                <!-- Actions -->
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <div class="flex items-center justify-end space-x-2">
                    <router-link
                      :to="`/rides?passenger=${passenger._id}`"
                      class="inline-flex items-center h-9 px-3 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                    >
                      <span class="mr-1 text-sm material-icons-round">history</span>
                      Trip History
                    </router-link>
                    <div @click.stop class="relative inline-block text-left">
                      <button
                        @click="toggleDropdown(index)"
                        type="button"
                        class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                        aria-haspopup="true"
                        :aria-expanded="openDropdown === index"
                      >
                        <i class="material-icons-round">more_vert</i>
                      </button>
                      <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                      >
                        <div
                          v-if="openDropdown === index"
                          class="absolute right-0 z-10 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                        >
                          <div
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                            class="py-1"
                          >
                            <router-link
                              :to="`passengers/${passenger._id}/edit`"
                              class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                              role="menuitem"
                            >
                              <i class="text-base material-icons-round">edit</i>
                              <span>Edit</span>
                            </router-link>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No passengers found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      allRiders: [],
      openDropdown: false,
      loading: true,
      q: "",
      range: {
        start: null,
        end: null,
      },
    };
  },
  components: {
    Pagination,
    ExportButton,
  },
  created() {
    this.getRiders();
  },
  computed: {
    passengers() {
      return this.allRiders;
    },
    reportDownloadUrl() {
      return (
        "users/export?" + Object.entries({
          ...Object.fromEntries(
            Object.entries({
              q: this.q,
              from: this.range.start ? this.range.start : undefined,
              to : this.range.end ? this.range.end : undefined,
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  watch: {
    currentPage() {
      this.getRiders();
    },
    range: {
      handler() {
        this.getRiders();
      },
      deep: true,
    },
    perPage() {
      this.currentPage === 1 ? this.getRiders() : (this.currentPage = 1);
    },
    q() {
      this.currentPage = 1;
      this.getRiders();
    },
  },
  methods: {
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    hideDropdown() {
      this.openDropdown = false;
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    getRiders() {
      this.loading = true;
      let path = `users/admin-search?q=${this.q}&limit=${this.perPage}&page=${this.currentPage}`;
      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }
      this.$misc
        .get(path)
        .then((response) => {
          this.allRiders = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
